import { smartWalletCreationPending, walletConnected, walletLoaded } from '$lib/wallet/store';
import type { AdjustMarginTitleType } from '$lib/popups/Margin/adjustMarginTypes';
import type { MarketIdentifiers } from '$lib/types/market';
import type { UserMargin } from '$lib/types/user';
import {
	LimitStatus,
	type InputUnit,
	type OrderExecutionType,
	type OrderSide
} from '$lib/types/userInteract';
import { PRICE_IMPACT_DELTA } from '$lib/web3/contants';
import BigNumber from 'bignumber.js';
import { writable, derived, type Writable, type Readable } from 'svelte/store';
import {
	currentMarket,
	currentMarketIdentifier,
	currentPosition,
	positions,
	orders,
	userBalances,
	maxLeverage,
	positionsToShow,
	postTradeDetails,
	tokensAllowedForDepositBalance
} from '$lib/home/store';
import { marketsConfig } from '$lib/config/markets';
import { addDollarSign, getNumBackInUnits } from '$lib/util/util';
import type { Order } from '$lib/types/order';
import {
	LIQUIDATION_PRICE_GAP_PERCENTAGE,
	MIN_MARGIN_REQUIREMENT,
	SAFE_MARGIN_REQUIREMENT
} from '../constants';
import { AUTOSWAP_OP, AUTOSWAP_STABLES } from '$lib/settings';
import type { Token, TokenSymbol } from '$lib/types/tokens';
import { nonStablesAllowedForDeposit, stablesAllowedForDeposit } from '../scwallet/config';
import persistentStorage from '$lib/persistentStorage';

export const longOrShort: Writable<OrderSide> = writable('Long');
export const longOrShortUpdatedSinceLoad: Writable<boolean> = writable(false);
export const executionType: Writable<OrderExecutionType> = writable('Market');
export const LoadingAccessibleMargin: Writable<boolean> = writable(false);
export const input: Writable<string> = writable('0');
export const marginInput: Writable<string> = writable('0');
export const depositInProgress: Writable<boolean> = writable(false);
export const withdrawInProgress: Writable<boolean> = writable(false);
export const activeSection: Writable<AdjustMarginTitleType> = writable('Deposit');
export const editingInputValue: Writable<boolean> = writable(false);
export const executingOrder: Writable<boolean> = writable(false);
export const closingPosition: Writable<boolean> = writable(false);

export const isAddMaxMargin: Writable<boolean> = writable(false);
export const userMarginList: Writable<Array<UserMargin>> = persistentStorage('user-margins', []);
export const firstLoadedMargin: Writable<boolean> = writable(false);
export const sumOfAllUserMargin: Readable<string> = derived(
	[userMarginList],
	([$userMarginList]) => {
		let sum = '0';
		$userMarginList.forEach((margin) => {
			sum = new BigNumber(margin.depositedMargin).plus(sum).toString();
		});
		return sum;
	}
);

export const expiredOrders: Readable<Array<Order>> = derived([orders], ([$orders]) => {
	if ($orders.data) {
		return $orders.data.filter((order) => {
			return order.status === 'Expired';
		});
	}

	return [];
});

export const marginsWithoutPositions: Readable<Array<UserMargin>> = derived(
	[userMarginList, positions, orders],
	([$userMarginList, $positions, $orders]) => {
		let userMargins: Array<UserMargin> = [];
		if (
			(!$positions || !$positions.data || $positions.data.length == 0) &&
			(!$orders || !$orders.data || $orders.data.length) == 0
		) {
			return $userMarginList.filter((margin) => {
				return !new BigNumber(margin.depositedMargin).isZero();
			});
		}

		$userMarginList.forEach((margin) => {
			let position;
			let order;

			if ($positions && $positions.data && $positions.data.length > 0) {
				position = $positions.data.find(
					(position) => position.marketIdentifier == margin.marketIdentifier
				);
			}

			if ($orders && $orders.data && $orders.data.length > 0) {
				order = $orders.data.find((order) => order.marketIdentifier == margin.marketIdentifier);
			}

			if (!position && !new BigNumber(margin.depositedMargin).isZero() && !order) {
				userMargins.push(margin);
			}
		});
		return userMargins;
	}
);

export const sumOfAllMarginsWithoutPositions: Readable<string> = derived(
	[marginsWithoutPositions],
	([$marginsWithoutPositions]) => {
		let sum = '0';
		$marginsWithoutPositions.forEach((margin) => {
			sum = new BigNumber(margin.depositedMargin).plus(sum).toString();
		});
		return sum;
	}
);

export const currentMarketMarginWithoutPostionOrOrders: Readable<string> = derived(
	[currentMarket, userMarginList, positions, orders],
	([$currentMarket, $userMarginList, $positions, $orders]) => {
		if ($currentMarket && $userMarginList) {
			const margin = $userMarginList.find(
				(margin) => margin.marketIdentifier == $currentMarket.marketIdentifier
			);
			if (margin?.depositedMargin) {
				let position;
				let order;

				if ($positions && $positions.data && $positions.data.length > 0) {
					position = $positions.data.find(
						(position) => position.marketIdentifier == margin.marketIdentifier
					);
				}

				if ($orders && $orders.data && $orders.data.length > 0) {
					order = $orders.data.find((order) => order.marketIdentifier == margin.marketIdentifier);
				}

				if (!position && !new BigNumber(margin.depositedMargin).isZero() && !order) {
					return margin.depositedMargin;
				}
			}
		}
		return '0';
	}
);

export const userMarginBalanceForTrade: Readable<string> = derived(
	[
		userBalances,
		sumOfAllMarginsWithoutPositions,
		currentMarketMarginWithoutPostionOrOrders,
		tokensAllowedForDepositBalance,
		AUTOSWAP_STABLES,
		AUTOSWAP_OP
	],
	([
		$userBalances,
		$sumOfAllMarginsWithoutPositions,
		$currentMarketMarginWithoutPostionOrOrders,
		$tokensAllowedForDepositBalance,
		$AUTOSWAP_STABLES,
		$AUTOSWAP_OP
	]) => {
		if ($userBalances?.sUSD?.balanceUSD && $sumOfAllMarginsWithoutPositions) {
			let balance: BigNumber = new BigNumber($userBalances?.sUSD?.balanceUSD)
				.plus($sumOfAllMarginsWithoutPositions)
				.minus($currentMarketMarginWithoutPostionOrOrders);

			if ($tokensAllowedForDepositBalance) {
				for (const token in $tokensAllowedForDepositBalance) {
					const currentToken: Token = $tokensAllowedForDepositBalance[token];
					if (token === 'sUSD' || !currentToken.balanceUSD) {
						continue;
					}

					if (!$AUTOSWAP_STABLES && stablesAllowedForDeposit.includes(token as TokenSymbol)) {
						continue;
					}

					if (!$AUTOSWAP_OP && nonStablesAllowedForDeposit.includes(token as TokenSymbol)) {
						continue;
					}
					balance = balance.plus(
						new BigNumber(currentToken.balanceUSD).multipliedBy(
							`1e${18 - parseInt(currentToken.decimals)}`
						)
					);
				}
			}

			return balance.toString();
		}

		return '0';
	}
);

export const adjustMarginMarket: Writable<MarketIdentifiers> = writable();

export const marginInputForTrade: Writable<string> = writable('0');
export const priceImpactDelta: Writable<string> = writable(PRICE_IMPACT_DELTA);
export const limitInputForTrade: Writable<string> = writable('0');
export const triggerInputForTrade: Writable<string> = writable('0');
export const limitOrderTriggerStatus: Writable<LimitStatus> = writable(LimitStatus.Ok);

export const takeProfitActive: Writable<boolean> = writable(false);
export const limitInputForTakeProfit: Writable<string> = writable('0');
export const triggerInputForTakeProfit: Writable<string> = writable('0');
export const TakeProfitStatus: Writable<LimitStatus> = writable(LimitStatus.Ok);

export const takeProfitInput: Writable<string> = writable('0');
export const stopLossInput: Writable<string> = writable('0');
export const gainPercentage: Writable<string> = writable('0');
export const lossPercentage: Writable<string> = writable('0');
export const isTPSLActive: Writable<boolean> = writable(false);
export const isEntirePosition: Writable<boolean> = writable(false);

export const limitOrderExpiry: Writable<number> = writable(2592000);
export const limitOrderExpiryTimestamp: Readable<string> = derived(
	[limitOrderExpiry],
	([$limitOrderExpiry]) => {
		const dateTime = new Date();
		const timeDelta = $limitOrderExpiry;
		let date_plus_delta = new Date(new Date(dateTime).getTime() + timeDelta * 1000);
		const expiry = new BigNumber(date_plus_delta.getTime() / 1000).toFixed(0).toString();
		return expiry;
	}
);

export const margin: Readable<string> = derived(
	[
		userMarginList,
		currentMarketIdentifier,
		marginInputForTrade,
		smartWalletCreationPending,
		walletLoaded,
		walletConnected
	],
	([
		$userMarginList,
		$currentMarketIdentifier,
		$marginInputForTrade,
		$smartWalletCreationPending,
		$walletLoaded,
		$walletConncted
	]) => {
		let ans;
		if ($userMarginList && $currentMarketIdentifier) {
			if (
				$marginInputForTrade == '0' ||
				$marginInputForTrade == '' ||
				$marginInputForTrade == undefined
			) {
				ans = '0';
			} else {
				ans = new BigNumber($marginInputForTrade);
			}

			const margin = $userMarginList.find(
				(margin) => margin.marketIdentifier == $currentMarketIdentifier
			);
			if (margin?.depositedMargin) {
				return new BigNumber(ans).plus(margin.depositedMargin).toString();
			} else if ($smartWalletCreationPending || ($walletLoaded && !$walletConncted)) {
				return ans.toString();
			}
			return '';
		}
		return '';
	}
);

export const accessibleMargin: Readable<string> = derived(
	[userMarginList, currentMarketIdentifier, marginInputForTrade],
	([$userMarginList, $currentMarketIdentifier, $marginInputForTrade]) => {
		if ($userMarginList && $currentMarketIdentifier) {
			let ans;
			if (
				$marginInputForTrade == '0' ||
				$marginInputForTrade == '' ||
				$marginInputForTrade == undefined
			) {
				ans = '0';
			} else {
				ans = new BigNumber($marginInputForTrade);
			}

			const margin = $userMarginList.find(
				(margin) => margin.marketIdentifier == $currentMarketIdentifier
			);
			if (margin?.accessibleMargin) {
				return new BigNumber(ans).plus(new BigNumber(margin.accessibleMargin)).toString();
			}
			return ans.toString();
		}
		return '';
	}
);

export const marginAdjustPopup: Readable<string> = derived(
	[userMarginList, adjustMarginMarket],
	([$userMarginList, $adjustMarginMarket]) => {
		if ($userMarginList && $adjustMarginMarket) {
			const margin = $userMarginList.find(
				(margin) => margin.marketIdentifier == $adjustMarginMarket
			);
			if (margin?.depositedMargin) {
				return margin.depositedMargin;
			}
			return '';
		}
		return '';
	}
);

export const accessibleMarginAdjustPopup: Readable<string> = derived(
	[userMarginList, adjustMarginMarket],
	([$userMarginList, $adjustMarginMarket]) => {
		if ($userMarginList && $adjustMarginMarket) {
			const margin = $userMarginList.find(
				(margin) => margin.marketIdentifier == $adjustMarginMarket
			);
			if (margin?.accessibleMargin) {
				return margin.accessibleMargin;
			}
			return '';
		}
		return '';
	}
);

export const inputUnit: Writable<InputUnit> = writable('USD');

export const cancellingAllOrders: Writable<boolean> = writable(false);
export const retryingOrder: Writable<boolean> = writable(false);
export const manuallyExecutingOrder: Writable<boolean> = writable(false);

export const mobileUserInteractDialogOpen: Writable<boolean> = writable(false);
export const validInput: Readable<boolean> = derived(
	[input, walletConnected],
	([$input, $walletConnected]) => {
		if (
			$input &&
			!new BigNumber($input).eq(0) &&
			$walletConnected &&
			!Number.isNaN(Number($input))
		) {
			return true;
		} else {
			return false;
		}
	}
);

export const accountLeverage: Readable<number> = derived(
	[margin, currentPosition, currentMarket, longOrShort],
	([$margin, $currentPosition, $currentMarket, $longOrShort]) => {
		if ($currentMarket && $currentPosition && $margin) {
			let accLeverage = new BigNumber($currentPosition.size)
				.multipliedBy($currentMarket.currentMarketPrice)
				.div(1e18)
				.dividedBy(new BigNumber($margin))
				.toNumber();
			if ($longOrShort == 'Short') {
				return accLeverage * -1;
			} else {
				return accLeverage;
			}
		} else {
			return 0;
		}
	}
);
export const leverage: Readable<number> = derived([input, margin], ([$input, $margin]) => {
	if ($input && $margin && new BigNumber($margin).gt(0)) {
		return new BigNumber($input).dividedBy(new BigNumber($margin)).toNumber();
	} else {
		return 0;
	}
});
export const newAccoutLeverage: Readable<number> = derived(
	[leverage, accountLeverage],
	([$leverage, $accountLeverage]) => {
		if ($leverage != undefined && $accountLeverage != undefined) {
			return $leverage + $accountLeverage;
		} else if ($accountLeverage) {
			return $accountLeverage;
		} else {
			return 0;
		}
	}
);
export const marginInputValid: Readable<boolean> = derived(
	[userMarginList, adjustMarginMarket, activeSection, marginInput, userBalances],
	([$userMarginList, adjustMarginMarket, $activeSection, $marginInput, $userBalances]) => {
		if ($userMarginList && adjustMarginMarket && $activeSection && $marginInput) {
			let marginInputBn = new BigNumber($marginInput);
			if ($activeSection == 'Deposit') {
				if (
					$userBalances &&
					marginInputBn.gt(0) &&
					marginInputBn.lte(new BigNumber($userBalances.sUSD.balanceUSD))
				) {
					return true;
				}
				return false;
			} else {
				let accessibleMargin = $userMarginList.find(
					(margin) => margin.marketIdentifier == adjustMarginMarket
				)?.accessibleMargin;

				if (!accessibleMargin) accessibleMargin = '0';

				if (marginInputBn.gt(0) && marginInputBn.lte(new BigNumber(accessibleMargin))) {
					return true;
				}
				return false;
			}
		} else {
			return false;
		}
	}
);
export const minInputAmount: Writable<string> = writable('0000000000000000000');
export const maxInputAmount: Readable<string> = derived(
	[longOrShort, currentPosition, currentMarket, maxLeverage, margin],
	([$longOrShort, $currentPosition, $currentMarket, $maxLeverage, $margin]) => {
		// return '2000000000000000000000';
		if ($currentPosition && $currentMarket) {
			if (
				(new BigNumber($currentPosition.size).gt(0) && $longOrShort == 'Long') ||
				(new BigNumber($currentPosition.size).lt(0) && $longOrShort == 'Short')
			) {
				return new BigNumber($margin)
					.multipliedBy($maxLeverage)
					.minus(
						new BigNumber($currentPosition.size)
							.multipliedBy($currentMarket.currentMarketPrice)
							.div(1e18)
							.abs()
					)
					.toString();
			} else {
				return new BigNumber($margin)
					.multipliedBy($maxLeverage)
					.plus(
						new BigNumber($currentPosition.size)
							.multipliedBy($currentMarket.currentMarketPrice)
							.div(1e18)
							.abs()
					)
					.toString();
			}
		} else if ($margin) {
			return new BigNumber($margin).multipliedBy($maxLeverage).toString();
		} else {
			return '0';
		}
	}
);

export const newPositionGradient: Readable<string> = derived(
	[newAccoutLeverage, longOrShort, maxLeverage],
	([$newAccountLeverage, $longOrShort, $maxLeverage]) => {
		if (new BigNumber($newAccountLeverage).gte(new BigNumber($maxLeverage).multipliedBy(0.95))) {
			return 'text-fx-yellow';
		}
		if (
			($newAccountLeverage > 0 && $longOrShort == 'Long') ||
			($newAccountLeverage < 0 && $longOrShort == 'Short')
		) {
			return 'text-fx-gradient-23';
		} else if (
			($newAccountLeverage < 0 && $longOrShort == 'Long') ||
			($newAccountLeverage > 0 && $longOrShort == 'Short')
		) {
			return 'text-fx-gradient-22';
		} else {
			return 'text-fx-grey-400';
		}
	}
);

export const estimatedProfit: Readable<string> = derived(
	[
		input,
		takeProfitInput,
		stopLossInput,
		currentMarket,
		longOrShort,
		executionType,
		triggerInputForTrade,
		postTradeDetails,
		isTPSLActive
	],
	([
		$input,
		$takeProfitInput,
		$stopLossInput,
		$currentMarket,
		$longOrShort,
		$executionType,
		$triggerInputForTrade,
		$postTradeDetails,
		$isTPSLActive
	]) => {
		if ($input && $currentMarket && $takeProfitInput) {
			if ($takeProfitInput == '0') return '0';
			let takeProfitInputBn = new BigNumber($takeProfitInput).multipliedBy(1e18);
			let currentPrice = new BigNumber(
				$currentMarket
					? $executionType == 'Limit'
						? $triggerInputForTrade
						: $currentMarket.safePrice
					: '0'
			);
			let inputBn = new BigNumber($input).div(currentPrice);

			let isTPSLOrder = $takeProfitInput != '0' || $stopLossInput != '0';

			let tpslOrderFee = isTPSLOrder
				? new BigNumber(getNumBackInUnits(new BigNumber($input).div(1e18).abs().toString(), 18, 2))
						.multipliedBy(getNumBackInUnits($postTradeDetails?.advancedOrderOrderFee || '0', 18, 8))
						.toString()
				: '0';

			let standardOrderFee = $postTradeDetails?.fee
				? new BigNumber($postTradeDetails.fee)
						.plus(new BigNumber($postTradeDetails.keeperFee))
						.toString()
				: '0';

			let totalFee = new BigNumber(standardOrderFee)
				.plus(new BigNumber(tpslOrderFee))
				.plus(isTPSLOrder ? new BigNumber($postTradeDetails?.advancedOrderFixedFee || '0') : '0')
				.plus(isTPSLOrder ? new BigNumber(standardOrderFee) : '0')
				.toString();

			if ($longOrShort == 'Long') {
				const estimatedProfit = takeProfitInputBn
					.minus(currentPrice)
					.multipliedBy(inputBn)
					.minus(new BigNumber(totalFee))
					.div(1e18)
					.toFixed(2)
					.toString();
				return estimatedProfit;
			} else {
				const esimatedProfit = currentPrice
					.minus(takeProfitInputBn)
					.multipliedBy(inputBn)
					.minus(new BigNumber(totalFee))
					.div(1e18)
					.toFixed(2)
					.toString();
				return esimatedProfit;
			}
		}
		return '0';
	}
);
export const estimatedLoss: Readable<string> = derived(
	[
		input,
		takeProfitInput,
		stopLossInput,
		currentMarket,
		longOrShort,
		executionType,
		triggerInputForTrade,
		postTradeDetails,
		isTPSLActive
	],
	([
		$input,
		$takeProfitInput,
		$stopLossInput,
		$currentMarket,
		$longOrShort,
		$executionType,
		$triggerInputForTrade,
		$postTradeDetails,
		$isTPSLActive
	]) => {
		if ($input && $currentMarket && $stopLossInput) {
			if ($stopLossInput == '0') return '0';
			let stopLossInputBn = new BigNumber($stopLossInput).multipliedBy(1e18);
			let currentPrice = new BigNumber(
				$currentMarket
					? $executionType == 'Limit'
						? $triggerInputForTrade
						: $currentMarket.safePrice
					: '0'
			);
			let inputBn = new BigNumber($input).div(currentPrice);

			let isTPSLOrder = $takeProfitInput != '0' || $stopLossInput != '0';

			let tpslOrderFee = isTPSLOrder
				? new BigNumber(getNumBackInUnits(new BigNumber($input).div(1e18).abs().toString(), 18, 2))
						.multipliedBy(getNumBackInUnits($postTradeDetails?.advancedOrderOrderFee || '0', 18, 8))
						.toString()
				: '0';

			let standardOrderFee = $postTradeDetails?.fee
				? new BigNumber($postTradeDetails.fee)
						.plus(new BigNumber($postTradeDetails.keeperFee))
						.toString()
				: '0';

			let totalFee = new BigNumber(standardOrderFee)
				.plus(new BigNumber(tpslOrderFee))
				.plus(isTPSLOrder ? new BigNumber($postTradeDetails?.advancedOrderFixedFee || '0') : '0')
				.plus(isTPSLOrder ? new BigNumber(standardOrderFee) : '0')
				.toString();

			if ($longOrShort == 'Long') {
				const estimatedLoss = currentPrice
					.minus(stopLossInputBn)
					.multipliedBy(inputBn)
					.plus(new BigNumber(totalFee))
					.div(1e18)
					.toFixed(2)
					.toString();
				return estimatedLoss;
			} else {
				const estimatedLoss = stopLossInputBn
					.minus(currentPrice)
					.multipliedBy(inputBn)
					.plus(new BigNumber(totalFee))
					.div(1e18)
					.toFixed(2)
					.toString();
				return estimatedLoss;
			}
		}
		return '0';
	}
);

export const isTakeProfitInputValid: Readable<boolean> = derived(
	[
		takeProfitInput,
		currentMarket,
		longOrShort,
		gainPercentage,
		executionType,
		triggerInputForTrade
	],
	([
		$takeProfitInput,
		$currentMarket,
		$longOrShort,
		$gainPercentage,
		$executionType,
		$triggerInputForTrade
	]) => {
		if ($executionType != 'Market') {
			if (new BigNumber($takeProfitInput).gt(0)) {
				if ($longOrShort === 'Long') {
					if (new BigNumber($takeProfitInput).lte(new BigNumber($triggerInputForTrade).div(1e18))) {
						takeProfitErrorMessage.set(
							`Min TP: ${addDollarSign(
								getNumBackInUnits(
									new BigNumber($triggerInputForTrade).toString(),
									18,
									$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
								),
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							)}`
						);
						return false;
					}
				} else {
					if (new BigNumber($takeProfitInput).gte(new BigNumber($triggerInputForTrade).div(1e18))) {
						takeProfitErrorMessage.set(
							`Max TP: ${addDollarSign(
								getNumBackInUnits(
									new BigNumber($triggerInputForTrade).toString(),
									18,
									$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
								),
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							)}`
						);
						return false;
					}
				}
			}

			return true;
		} else if (
			$currentMarket &&
			new BigNumber($takeProfitInput).gt(0) &&
			!new BigNumber($gainPercentage).eq(0)
		) {
			if ($currentMarket.safePrice) {
				if ($longOrShort == 'Long') {
					if (
						new BigNumber($takeProfitInput).lte(
							new BigNumber($currentMarket.safePrice)
								.div(1e18)
								.toFixed(marketsConfig[$currentMarket.asset]?.showDecimals)
						)
					) {
						takeProfitErrorMessage.set(
							`Min TP: ${addDollarSign(
								getNumBackInUnits(
									new BigNumber($currentMarket.safePrice).toString(),
									18,
									$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
								),
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							)}`
						);
						return false;
					}
				} else {
					if (
						new BigNumber($takeProfitInput).gte(
							new BigNumber($currentMarket.safePrice)
								.div(1e18)
								.toFixed(marketsConfig[$currentMarket.asset]?.showDecimals)
						)
					) {
						takeProfitErrorMessage.set(
							`Max TP: ${addDollarSign(
								getNumBackInUnits(
									new BigNumber($currentMarket.safePrice).toString(),
									18,
									$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
								),
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							)}`
						);

						return false;
					}
				}
			}
		}
		return true;
	}
);

export const isStopLossInputValid: Readable<boolean> = derived(
	[
		stopLossInput,
		currentMarket,
		longOrShort,
		lossPercentage,
		executionType,
		triggerInputForTrade,
		postTradeDetails,
		currentPosition,
		margin,
		estimatedLoss
	],
	([
		$stopLossInput,
		$currentMarket,
		$longOrShort,
		$lossPercentage,
		$executionType,
		$triggerInputForTrade,
		$postTradeDetails,
		$currentPosition,
		$margin,
		$estimatedLoss
	]) => {
		const liquidationPrice = $postTradeDetails
			? $postTradeDetails.liquidationPrice
			: $currentPosition
			? $currentPosition?.liquidationPrice
			: 0;
		if (new BigNumber($stopLossInput).gt(0) && new BigNumber(liquidationPrice).gt(0)) {
			if (
				$longOrShort === 'Long' &&
				new BigNumber($stopLossInput).lte(new BigNumber(liquidationPrice).div(1e18))
			) {
				stopLossErrorMessage.set('SL cannot be less than liq. price');
				stopLossErrorDescription.set('');
				return false;
			} else if (
				$longOrShort === 'Short' &&
				new BigNumber($stopLossInput).gte(new BigNumber(liquidationPrice).div(1e18))
			) {
				stopLossErrorMessage.set('SL cannot be greater than liq. price');
				stopLossErrorDescription.set('');
				return false;
			}
		}
		if ($executionType != 'Market' && new BigNumber($stopLossInput).gt(0)) {
			if ($longOrShort === 'Long') {
				if (new BigNumber($stopLossInput).gte(new BigNumber($triggerInputForTrade).div(1e18))) {
					stopLossErrorMessage.set(
						`Max SL: ${addDollarSign(
							getNumBackInUnits(
								new BigNumber($triggerInputForTrade).toString(),
								18,
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							),
							$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
						)}`
					);
					stopLossErrorDescription.set('');
					return false;
				}
			} else {
				if (new BigNumber($stopLossInput).lte(new BigNumber($triggerInputForTrade).div(1e18))) {
					stopLossErrorMessage.set(
						`Min SL: ${addDollarSign(
							getNumBackInUnits(
								new BigNumber($triggerInputForTrade).toString(),
								18,
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							),
							$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
						)}`
					);
					stopLossErrorDescription.set('');
					return false;
				}
			}
		}
		if (
			$executionType == 'Market' &&
			$currentMarket &&
			new BigNumber($stopLossInput).gt(0) &&
			!new BigNumber($lossPercentage).eq(0)
		) {
			if ($currentMarket.safePrice) {
				if ($longOrShort == 'Long') {
					if (
						new BigNumber($stopLossInput).gte(
							new BigNumber($currentMarket.safePrice)
								.div(1e18)
								.toFixed(marketsConfig[$currentMarket.asset]?.showDecimals)
						)
					) {
						stopLossErrorMessage.set(
							`Max SL: ${addDollarSign(
								getNumBackInUnits(
									new BigNumber($currentMarket.safePrice).toString(),
									18,
									$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
								),
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							)}`
						);
						stopLossErrorDescription.set('');
						return false;
					}
				} else {
					if (
						new BigNumber($stopLossInput).lte(
							new BigNumber($currentMarket.safePrice)
								.div(1e18)
								.toFixed(marketsConfig[$currentMarket.asset]?.showDecimals)
						)
					) {
						stopLossErrorMessage.set(
							`Min SL: ${addDollarSign(
								getNumBackInUnits(
									new BigNumber($currentMarket.safePrice).toString(),
									18,
									$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
								),
								$currentMarket ? marketsConfig[$currentMarket?.asset].showDecimals : 4
							)}`
						);
						stopLossErrorDescription.set('');
						return false;
					}
				}
			}
		}
		if (
			new BigNumber(getNumBackInUnits($margin, 18))
				.minus(new BigNumber($estimatedLoss))
				.lt(new BigNumber(MIN_MARGIN_REQUIREMENT))
		) {
			stopLossErrorMessage.set('SL below min. margin requirement');
			stopLossErrorDescription.set(
				`Your margin at the time of Stop Loss will be less than the minimum margin requirement of $40. Please increase your margin or adjust your SL accordingly.`
			);
			return false;
		}
		stopLossErrorMessage.set('');
		stopLossErrorDescription.set('');
		return true;
	}
);

export const isStopLossInputSafe: Readable<boolean> = derived(
	[stopLossInput, postTradeDetails, currentPosition, margin, estimatedLoss],
	([$stopLossInput, $postTradeDetails, $currentPosition, $margin, $estimatedLoss]) => {
		if (new BigNumber($stopLossInput).gt(0)) {
			const liquidationPrice = $postTradeDetails
				? $postTradeDetails.liquidationPrice
				: $currentPosition
				? $currentPosition?.liquidationPrice
				: 0;
			if (new BigNumber(liquidationPrice).gt(0)) {
				const stopLossGapInPercentage = new BigNumber($stopLossInput)
					.minus(new BigNumber(liquidationPrice).div(1e18))
					.abs()
					.div(new BigNumber(liquidationPrice).div(1e18))
					.times(100)
					.toFixed(2);
				if (
					new BigNumber(stopLossGapInPercentage).lt(new BigNumber(LIQUIDATION_PRICE_GAP_PERCENTAGE))
				) {
					stopLossWarningMessage.set('SL too close to Liq. Price');
					return false;
				}
			}
			if (
				new BigNumber(getNumBackInUnits($margin, 18))
					.minus(new BigNumber($estimatedLoss))
					.lt(new BigNumber(SAFE_MARGIN_REQUIREMENT))
			) {
				stopLossWarningMessage.set('SL too close to Min. Margin');
				return false;
			}
		}
		return true;
	}
);

export const takeProfitErrorMessage: Writable<string> = writable('');
export const stopLossErrorMessage: Writable<string> = writable('');
export const stopLossErrorDescription: Writable<string> = writable('');
export const stopLossWarningMessage: Writable<string> = writable('');
